import React, { useState, useEffect, useRef } from 'react';
import { 
  FaUsers, 
  FaDownload, 
  FaTimes, 
  FaSearch, 
  FaFilter, 
  FaChevronLeft, 
  FaChevronRight,
  FaUserEdit,
  FaExchangeAlt,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaFolderOpen,
  FaProjectDiagram,
  FaUserPlus,
  FaUserCog
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';

import styles from './ExternalPersonnelManagementPage.module.css';
import './ExternalPersonnelDarkMode.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../../components/TableFilter';
import { useTheme } from '../../contexts/ThemeContext';
import AddExternalPersonnelModal from './AddExternalPersonnelModal';
import EditExternalPersonnelModal from './EditExternalPersonnelModal';

const truncateText = (text, maxLength = 15) => {
  if (!text || typeof text !== 'string') return '-';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const ExternalPersonnelManagementPage = () => {
  const { isDarkMode } = useTheme();
  const [externalPersonnels, setExternalPersonnels] = useState([]);
  const [filteredPersonnels, setFilteredPersonnels] = useState([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filters, setFilters] = useState({ 'Durum': 'aktif' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // Fetch 10 personnel per page
  const [totalPersonnels, setTotalPersonnels] = useState(0);
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);
  const [pageCache, setPageCache] = useState(() => {
    // Initialize from localStorage if available
    try {
      const cached = localStorage.getItem('externalPersonnelsPageCache');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.error('Error loading cache:', e);
      return {};
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  // Filtering states
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [allPersonnelsForFilter, setAllPersonnelsForFilter] = useState([]);
  const [statsData, setStatsData] = useState({ active: 0, inactive: 0 });

  const [visibleColumns, setVisibleColumns] = useState({
    'Adı': true,
    'Soyadı': true,
    'Adres': false,
    'Telefon': true,
    'E-Posta': true,
    'Kurum': true,
    'Ünvan': true,
    'Uzmanlık': false,
    'Ana Bilim Dalı': false,
    'Personel Tipi': true,
    'Durum': true
  });

  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  const columns = {
    'Adı': { label: 'Adı', type: 'text' },
    'Soyadı': { label: 'Soyadı', type: 'text' },
    'Adres': { label: 'Adres', type: 'text' },
    'Telefon': { label: 'Telefon', type: 'text' },
    'E-Posta': { label: 'E-Posta', type: 'text' },
    'Kurum': { label: 'Kurum', type: 'text' },
    'Ünvan': { label: 'Ünvan', type: 'text' },
    'Uzmanlık': { label: 'Uzmanlık', type: 'text' },
    'Ana Bilim Dalı': { label: 'Ana Bilim Dalı', type: 'text' },
    'Personel Tipi': { label: 'Personel Tipi', type: 'text' },
    'Durum': {
      label: 'Durum',
      type: 'select',
      options: [
        { value: '', label: 'Tümü' },
        { value: 'aktif', label: 'Aktif' },
        { value: 'inaktif', label: 'İnaktif' }
      ]
    }
  };

  // Fetch all external personnels for filtering/searching
  const fetchAllPersonnelsForFilter = async () => {
    try {
      const allPersonnelsQuery = query(collection(db, 'ExternalPersonnels'), orderBy('Adı'));
      const querySnapshot = await getDocs(allPersonnelsQuery);
      const allPersonnels = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllPersonnelsForFilter(allPersonnels);
      setTotalPersonnels(allPersonnels.length);
      return allPersonnels;
    } catch (error) {
      console.error('Error fetching all external personnels:', error);
      return [];
    }
  };

  // Fetch total count and stats
  const fetchTotalCount = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'ExternalPersonnels'));
      const total = querySnapshot.size;
      setTotalPersonnels(total);

      // Calculate stats from the fetched data
      const allDocs = querySnapshot.docs.map(doc => doc.data());
      const active = allDocs.filter(p => p['Durum'] === 'aktif').length;
      const inactive = allDocs.filter(p => p['Durum'] === 'inaktif').length;
      setStatsData({ active, inactive });
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  };

  // Fetch external personnels with pagination
  const fetchExternalPersonnels = async (page = 1, useCache = true) => {
    // Check cache first
    if (useCache && pageCache[page]) {
      setExternalPersonnels(pageCache[page]);
      setCurrentPage(page);
      return;
    }

    setIsLoading(true);
    try {
      let personnelsQuery;

      if (page === 1) {
        // First page query
        personnelsQuery = query(
          collection(db, 'ExternalPersonnels'),
          orderBy('Adı'),
          limit(pageSize)
        );
      } else if (page > currentPage && lastVisible) {
        // Next page query
        personnelsQuery = query(
          collection(db, 'ExternalPersonnels'),
          orderBy('Adı'),
          startAfter(lastVisible),
          limit(pageSize)
        );
      } else if (page < currentPage && firstVisible) {
        // Previous page query - use cache or refetch from page 1
        if (pageCache[page]) {
          setExternalPersonnels(pageCache[page]);
          setCurrentPage(page);
          setIsLoading(false);
          return;
        }
        // Fallback: refetch from beginning
        personnelsQuery = query(
          collection(db, 'ExternalPersonnels'),
          orderBy('Adı'),
          limit(pageSize * page)
        );
      } else {
        // Default to first page
        personnelsQuery = query(
          collection(db, 'ExternalPersonnels'),
          orderBy('Adı'),
          limit(pageSize)
        );
      }

      const querySnapshot = await getDocs(personnelsQuery);

      let personnelsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      // If fetching multiple pages for previous page navigation
      if (page < currentPage && page > 1) {
        // Get only the last pageSize items
        personnelsData = personnelsData.slice(-pageSize);
      }

      // Store first and last visible documents for pagination
      if (querySnapshot.docs.length > 0) {
        setFirstVisible(querySnapshot.docs[0]);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }

      setExternalPersonnels(personnelsData);
      setCurrentPage(page);

      // Cache the page in state and localStorage
      setPageCache(prev => {
        const newCache = {
          ...prev,
          [page]: personnelsData
        };
        try {
          localStorage.setItem('externalPersonnelsPageCache', JSON.stringify(newCache));
        } catch (e) {
          console.error('Error saving cache:', e);
        }
        return newCache;
      });

    } catch (error) {
      console.error('Error fetching external personnels:', error);
      toast.error('Dış personeller yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Clear cache when data changes
  const refreshExternalPersonnels = () => {
    setPageCache({});
    try {
      localStorage.removeItem('externalPersonnelsPageCache');
    } catch (e) {
      console.error('Error clearing cache:', e);
    }
    setCurrentPage(1);
    // Refresh both all personnels (for filtering) and paginated personnels
    fetchAllPersonnelsForFilter().then(() => {
      fetchExternalPersonnels(1, false);
    });
  };

  useEffect(() => {
    // Only fetch first page on initial load (don't fetch all personnels until needed)
    fetchExternalPersonnels(1, false);
    fetchTotalCount();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  // Helper function to normalize Turkish characters
  const normalizeTurkishText = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .replace(/İ/g, 'i')
      .replace(/I/g, 'ı')
      .replace(/Ü/g, 'ü')
      .replace(/Ö/g, 'ö')
      .replace(/Ç/g, 'ç')
      .replace(/Ş/g, 'ş')
      .replace(/Ğ/g, 'ğ');
  };

  useEffect(() => {
    // Check if any filters or search are active (with minimum 3 characters for search)
    const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
    const hasActiveFilters = Object.keys(filters).some(key => {
      // Durum filter with value 'aktif' is default, so don't count it as active filter
      if (key === 'Durum' && filters[key] === 'aktif') return false;
      // For text filters, require at least 3 characters
      if (filters[key] && typeof filters[key] === 'string' && key !== 'Durum') {
        return filters[key].trim().length >= 3;
      }
      return filters[key] && filters[key] !== '';
    });

    const isAnyFilterActive = hasActiveSearch || hasActiveFilters;
    setIsFilterActive(isAnyFilterActive);

    // Fetch all personnels if filter is active and we haven't fetched them yet
    if (isAnyFilterActive && allPersonnelsForFilter.length === 0) {
      fetchAllPersonnelsForFilter();
    }

    // Use all personnels when filter/search is active, otherwise use paginated personnels
    let sourceData = isAnyFilterActive ? allPersonnelsForFilter : externalPersonnels;
    let filtered = sourceData;

    // Apply search term first (minimum 3 characters)
    if (searchTerm && searchTerm.trim().length >= 3) {
      const lowercasedSearch = normalizeTurkishText(searchTerm);
      filtered = filtered.filter(
        personnel =>
          (personnel['Adı'] && normalizeTurkishText(personnel['Adı']).includes(lowercasedSearch)) ||
          (personnel['Soyadı'] && normalizeTurkishText(personnel['Soyadı']).includes(lowercasedSearch)) ||
          (personnel['Telefon'] && normalizeTurkishText(personnel['Telefon']).includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'Durum') {
          // Durum filter always applies (no minimum length)
          filtered = filtered.filter(personnel =>
            normalizeTurkishText(personnel[key]) === normalizeTurkishText(filters[key])
          );
        } else {
          // Text filters require minimum 3 characters
          const filterValue = String(filters[key]);
          if (filterValue.trim().length >= 3) {
            filtered = filtered.filter(personnel =>
              normalizeTurkishText(String(personnel[key] || ''))
                .includes(normalizeTurkishText(filterValue))
            );
          }
        }
      }
    });

    setFilteredPersonnels(filtered);
  }, [externalPersonnels, allPersonnelsForFilter, filters, searchTerm]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handlePersonnelSelect = (personnel) => {
    setSelectedPersonnel(personnel);
  };

  const handleStatusToggle = async () => {
    if (!selectedPersonnel) return;
    
    const newStatus = selectedPersonnel['Durum'] === 'Aktif' ? 'İnaktif' : 'Aktif';
    
    try {
      const personnelRef = doc(db, 'ExternalPersonnels', selectedPersonnel.id);
      await updateDoc(personnelRef, { 'Durum': newStatus });
      toast.success(`Personel durumu ${newStatus} olarak güncellendi`);

      // Update local state
      refreshExternalPersonnels();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const downloadExcel = async () => {
    try {
      // Fetch all personnels for export
      toast.info('Excel dosyası hazırlanıyor...');
      const allPersonnelsQuery = query(collection(db, 'ExternalPersonnels'), orderBy('Adı'));
      const allPersonnelsSnapshot = await getDocs(allPersonnelsQuery);
      const allPersonnels = allPersonnelsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Dış Personeller');

      // Define columns based on visible columns
      const excelColumns = Object.keys(columns)
        .filter(key => visibleColumns[key])
        .map(key => ({
          header: columns[key].label,
          key: key.replace(/\s+/g, '_').toLowerCase(),
          width: 15
        }));

      worksheet.columns = excelColumns;

      // Add data
      allPersonnels.forEach(personnel => {
        const rowData = {};
        Object.keys(columns)
          .filter(key => visibleColumns[key])
          .forEach(key => {
            rowData[key.replace(/\s+/g, '_').toLowerCase()] = personnel[key] || '';
          });
        
        worksheet.addRow(rowData);
      });

      // Style the header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, size: 16, name: 'Calibri' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '92D050' }
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Style data cells
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.font = { size: 13, name: 'Calibri' };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'left',
              wrapText: true
            };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        }
      });

      // Add filter to header row
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columns.length }
      };

      const date = new Date();
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DisPersoneller_${formattedDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Dosya indirilirken hata oluştu');
    }
  };

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    e.stopPropagation();
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check
      
      container.addEventListener('scroll', handleScroll);
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [filteredPersonnels]);

  // Use stats data fetched separately for accurate counts
  const activeCount = statsData.active;
  const inactiveCount = statsData.inactive;
  const totalPages = Math.ceil(totalPersonnels / pageSize);

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      fetchExternalPersonnels(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      fetchExternalPersonnels(currentPage - 1);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchExternalPersonnels(page);
    }
  };

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaUsers className={styles.headerIcon} />
              Dış Personel Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button 
                className={styles.downloadButton}
                onClick={downloadExcel}
              >
                <FaDownload className={styles.buttonIcon} />
                Tabluyo İndir
              </button>
              <button 
                className={styles.addButton}
                onClick={() => setShowAddModal(true)}
              >
                <FaUserPlus className={styles.buttonIcon} />
                Yeni Harici Personel Ekle
              </button>
            </div>
          </div>
        </header>

        <div className={styles.dashboard}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{totalPersonnels}</p>
                  <h3 className={styles.cardTitle}>Toplam Dış Personel</h3>
                </div>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif Personel</h3>
                </div>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>İnaktif Personel</h3>
                </div>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="İsim, soyisim veya telefon ile ara (min. 3 karakter)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
              {searchTerm && searchTerm.length > 0 && searchTerm.length < 3 && (
                <span className={styles.searchHint}>
                  En az 3 karakter girin
                </span>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4>Sütun Görünürlüğü</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(colKey => (
                        <div key={colKey} className={styles.columnCheckbox}>
                          <input
                            type="checkbox"
                            id={`col-${colKey}`}
                            checked={visibleColumns[colKey]}
                            onChange={() => toggleColumnVisibility(colKey)}
                          />
                          <label htmlFor={`col-${colKey}`}>
                            {columns[colKey].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedPersonnel && (
          <div className={styles.actionPanel}>
            <div className={styles.selectedPersonnelInfo}>
              <h3 className={styles.selectedName}>
                {selectedPersonnel['Adı']} {selectedPersonnel['Soyadı']}
              </h3>
              <span className={`${styles.statusBadge} ${
                selectedPersonnel['Durum']?.toLowerCase() === 'aktif' 
                  ? styles.statusActive 
                  : styles.statusInactive
              }`}>
                {selectedPersonnel['Durum']}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button 
                className={`${styles.actionButton} ${styles.editButton}`}
                onClick={() => setShowEditModal(true)}
              >
                <FaUserEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>
              
              {/* <button 
                className={`${styles.actionButton} ${styles.statusButton}`} 
                onClick={handleStatusToggle}
              >
                <FaExchangeAlt className={styles.actionIcon} />
                <span>Durum Değiştir</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.salaryButton}`}>
                <FaMoneyBillWave className={styles.actionIcon} />
                <span>Ödemeler</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.paymentsButton}`}>
                <FaFileInvoiceDollar className={styles.actionIcon} />
                <span>Faturalar</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.filesButton}`}>
                <FaFolderOpen className={styles.actionIcon} />
                <span>Dosyalar</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.projectsButton}`}>
                <FaProjectDiagram className={styles.actionIcon} />
                <span>Projeler</span>
              </button> */}
            </div>
          </div>
        )}

        <section className={styles.tableSection}>
          {/* Pagination Controls - Hidden when filters are active */}
          {!isFilterActive && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                <span>
                  Sayfa {currentPage} / {totalPages} ({totalPersonnels} toplam personel)
                </span>
                {isLoading && <span className={styles.loadingIndicator}> Yükleniyor...</span>}
              </div>
              <div className={styles.paginationControls}>
                <button
                  className={styles.paginationButton}
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1 || isLoading}
                >
                  İlk
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1 || isLoading}
                >
                  <FaChevronLeft /> Önceki
                </button>
                <span className={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={`${styles.pageNumber} ${currentPage === pageNum ? styles.activePage : ''}`}
                        onClick={() => goToPage(pageNum)}
                        disabled={isLoading}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </span>
                <button
                  className={styles.paginationButton}
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages || isLoading}
                >
                  Sonraki <FaChevronRight />
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages || isLoading}
                >
                  Son
                </button>
              </div>
            </div>
          )}

          {/* Show filter info when filters are active */}
          {isFilterActive && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                <span>
                  {filteredPersonnels.length} sonuç bulundu (toplam {totalPersonnels} personel içinden)
                </span>
              </div>
            </div>
          )}

          <div className={styles.tableWrapper}>
            <div className={styles.tableScrollContainer}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={styles.tableContainer}
                onScroll={handleScroll}
              >
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        visibleColumns[key] && (
                          <th key={key}>
                            {col.label}
                            <TableFilter 
                              column={key} 
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPersonnels.length > 0 ? (
                      filteredPersonnels.map((personnel) => (
                        <tr 
                          key={personnel.id} 
                          onClick={() => handlePersonnelSelect(personnel)}
                          className={selectedPersonnel && selectedPersonnel.id === personnel.id ? styles.selectedRow : ''}
                        >
                          {Object.keys(columns).map(key => 
                            visibleColumns[key] && (
                              <td key={key} title={personnel[key]}>
                                {key === 'Durum' ? (
                                  <span className={`${styles.statusBadge} ${
                                    personnel['Durum']?.toLowerCase() === 'aktif'
                                      ? styles.statusActive
                                      : styles.statusInactive
                                  }`}>
                                    {personnel['Durum'] || '-'}
                                  </span>
                                ) : (
                                  truncateText(personnel[key])
                                )}
                              </td>
                            )
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaUsers className={styles.noDataIcon} />
                            <p>Filtrelere uygun personel bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />

      {showAddModal && (
        <AddExternalPersonnelModal
          onClose={() => setShowAddModal(false)}
          onUpdate={() => {
            // Refresh the personnel list after adding new personnel
            refreshExternalPersonnels();
            setShowAddModal(false);
          }}
        />
      )}

      {showEditModal && selectedPersonnel && (
        <EditExternalPersonnelModal
          personnel={selectedPersonnel}
          onClose={() => setShowEditModal(false)}
          onUpdate={() => {
            // Refresh the personnel list after editing personnel
            refreshExternalPersonnels();
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ExternalPersonnelManagementPage; 